import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, Edit, Trash2, Plus, ArrowLeft, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import LeagueSelector from '../components/admin/LeagueSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTables = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sync');
  const [loading, setLoading] = useState(false);

  // Data states
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [bookmakers, setBookmakers] = useState([]);
  const [markets, setMarkets] = useState([]);

  // Dialog states
  const [leagueDialog, setLeagueDialog] = useState(false);
  const [teamDialog, setTeamDialog] = useState(false);
  const [bookmakerDialog, setBookmakerDialog] = useState(false);
  const [marketDialog, setMarketDialog] = useState(false);

  // Form states
  const [leagueForm, setLeagueForm] = useState({ id: null, name: '', country: '', sport: 'Football' });
  const [teamForm, setTeamForm] = useState({ id: null, name: '', league_id: '' });
  const [bookmakerForm, setBookmakerForm] = useState({ id: null, name: '' });
  const [marketForm, setMarketForm] = useState({ id: null, name: '', description: '' });

  // Initialize data
  const [initializing, setInitializing] = useState(false);
  
  // API Football sync state
  const [syncing, setSyncing] = useState(false);

  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null,
    variant: 'default'
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    try {
      // Always fetch leagues (needed for Teams dropdown)
      const leaguesRes = await axios.get(`${API}/admin/leagues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeagues(leaguesRes.data);
      
      if (activeTab === 'teams') {
        const teamsRes = await axios.get(`${API}/admin/teams`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeams(teamsRes.data);
      }
      
      if (activeTab === 'bookmakers') {
        const bookmakersRes = await axios.get(`${API}/admin/bookmakers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBookmakers(bookmakersRes.data);
      }
      
      if (activeTab === 'markets') {
        const marketsRes = await axios.get(`${API}/admin/markets`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMarkets(marketsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // League CRUD
  const handleSaveLeague = async () => {
    const token = localStorage.getItem('session_token');
    try {
      if (leagueForm.id) {
        await axios.put(`${API}/admin/leagues/${leagueForm.id}`, {
          name: leagueForm.name,
          country: leagueForm.country,
          sport: leagueForm.sport
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('League updated successfully');
      } else {
        await axios.post(`${API}/admin/leagues`, {
          name: leagueForm.name,
          country: leagueForm.country,
          sport: leagueForm.sport
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('League created successfully');
      }
      setLeagueDialog(false);
      setLeagueForm({ id: null, name: '', country: '', sport: 'Football' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save league');
    }
  };

  const handleDeleteLeague = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete League',
      description: 'Delete this league? All associated teams will also be deleted.',
      variant: 'destructive',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        const token = localStorage.getItem('session_token');
        try {
          await axios.delete(`${API}/admin/leagues/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('League deleted successfully');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete league');
        }
      }
    });
  };

  // Sync API-Football Data
  const handleSyncAPIFootball = async () => {
    setConfirmDialog({
      open: true,
      title: 'Update from API-Football',
      description: 'This will fetch all leagues and teams from API-Football and replace existing team-league relationships. This may take a few minutes. Continue?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        const token = localStorage.getItem('session_token');
        setSyncing(true);
        
        const loadingToast = toast.loading('Syncing data from API-Football...');
        
        try {
          const response = await axios.post(`${API}/admin/sync-api-football`, {}, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 300000 // 5 minutes timeout
          });
          
          const stats = response.data.stats;
          toast.dismiss(loadingToast);
          toast.success(
            `Sync complete! Leagues: ${stats.leagues_added} added, ${stats.leagues_updated} updated. Teams: ${stats.teams_added} added.`,
            { duration: 6000 }
          );
          
          // Refresh data
          fetchData();
        } catch (error) {
          toast.dismiss(loadingToast);
          console.error('Sync error:', error);
          toast.error(error.response?.data?.detail || 'Failed to sync data from API-Football');
        } finally {
          setSyncing(false);
        }
      }
    });
  };

  // Team CRUD
  const handleSaveTeam = async () => {
    const token = localStorage.getItem('session_token');
    try {
      if (teamForm.id) {
        await axios.put(`${API}/admin/teams/${teamForm.id}`, {
          name: teamForm.name,
          league_id: teamForm.league_id
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Team updated successfully');
      } else {
        await axios.post(`${API}/admin/teams`, {
          name: teamForm.name,
          league_id: teamForm.league_id
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Team created successfully');
      }
      setTeamDialog(false);
      setTeamForm({ id: null, name: '', league_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save team');
    }
  };

  const handleDeleteTeam = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Team',
      description: 'Delete this team?',
      variant: 'destructive',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        const token = localStorage.getItem('session_token');
        try {
          await axios.delete(`${API}/admin/teams/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Team deleted successfully');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete team');
        }
      }
    });
  };

  // Bookmaker CRUD
  const handleSaveBookmaker = async () => {
    const token = localStorage.getItem('session_token');
    try {
      if (bookmakerForm.id) {
        await axios.put(`${API}/admin/bookmakers/${bookmakerForm.id}`, {
          name: bookmakerForm.name
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Bookmaker updated successfully');
      } else {
        await axios.post(`${API}/admin/bookmakers`, {
          name: bookmakerForm.name
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Bookmaker created successfully');
      }
      setBookmakerDialog(false);
      setBookmakerForm({ id: null, name: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save bookmaker');
    }
  };

  const handleDeleteBookmaker = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Bookmaker',
      description: 'Delete this bookmaker?',
      variant: 'destructive',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        const token = localStorage.getItem('session_token');
        try {
          await axios.delete(`${API}/admin/bookmakers/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Bookmaker deleted successfully');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete bookmaker');
        }
      }
    });
  };

  // Market CRUD
  const handleSaveMarket = async () => {
    const token = localStorage.getItem('session_token');
    try {
      if (marketForm.id) {
        await axios.put(`${API}/admin/markets/${marketForm.id}`, {
          name: marketForm.name,
          description: marketForm.description
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Market updated successfully');
      } else {
        await axios.post(`${API}/admin/markets`, {
          name: marketForm.name,
          description: marketForm.description
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Market created successfully');
      }
      setMarketDialog(false);
      setMarketForm({ id: null, name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save market');
    }
  };

  const handleDeleteMarket = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Market',
      description: 'Delete this market?',
      variant: 'destructive',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        const token = localStorage.getItem('session_token');
        try {
          await axios.delete(`${API}/admin/markets/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Market deleted successfully');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete market');
        }
      }
    });
  };

  // Reorder functions
  const handleMoveItem = async (type, items, setItems, index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    // Swap items in local state
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setItems(newItems);

    // Build order object with all items
    const order = {};
    newItems.forEach((item, idx) => {
      order[item.id] = idx;
    });

    // Send to backend
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(`${API}/admin/${type}/reorder`, order, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order updated successfully');
    } catch (error) {
      toast.error('Failed to update order');
      // Revert on error
      fetchData();
    }
  };

  // Initialize tables with existing data
  const handleInitializeTables = async () => {
    setConfirmDialog({
      open: true,
      title: 'Initialize Tables',
      description: 'Initialize tables with default leagues, bookmakers, and markets? This will not affect existing data.',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setInitializing(true);
        const token = localStorage.getItem('session_token');
        try {
          const response = await axios.post(`${API}/admin/initialize-tables`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Initialize response:', response.data);
          
          // Show detailed success message
          const { added, total } = response.data;
          toast.success(
            `Initialized! Added ${added.leagues} leagues, ${added.bookmakers} bookmakers, ${added.markets} markets. Total: ${total.leagues} leagues, ${total.bookmakers} bookmakers, ${total.markets} markets`,
            { duration: 5000 }
          );
          
          // Refresh all data
          await fetchAllData();
        } catch (error) {
          console.error('Initialize error:', error);
          console.error('Error response:', error.response?.data);
          toast.error(error.response?.data?.detail || 'Failed to initialize tables');
        } finally {
          setInitializing(false);
        }
      }
    });
  };

  // Fetch all data regardless of active tab
  const fetchAllData = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const [leaguesRes, teamsRes, bookmakersRes, marketsRes] = await Promise.all([
        axios.get(`${API}/admin/leagues`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/teams`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookmakers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/markets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setLeagues(leaguesRes.data);
      setTeams(teamsRes.data);
      setBookmakers(bookmakersRes.data);
      setMarkets(marketsRes.data);
    } catch (error) {
      console.error('Error fetching all data:', error);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white">Manage Tables</h1>
              <p className="text-gray-400">Manage leagues, teams, bookmakers, and markets</p>
            </div>
            <Button
              onClick={handleInitializeTables}
              disabled={initializing}
              className="bg-green-600 hover:bg-green-700"
            >
              {initializing ? 'Initializing...' : 'Initialize Data'}
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            <Button
              onClick={() => setActiveTab('sync')}
              variant={activeTab === 'sync' ? 'default' : 'outline'}
              className={activeTab === 'sync' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync Leagues
            </Button>
            <Button
              onClick={() => setActiveTab('leagues')}
              variant={activeTab === 'leagues' ? 'default' : 'outline'}
              className={activeTab === 'leagues' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              <Table className="w-4 h-4 mr-2" />
              Leagues
            </Button>
            <Button
              onClick={() => setActiveTab('teams')}
              variant={activeTab === 'teams' ? 'default' : 'outline'}
              className={activeTab === 'teams' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              <Table className="w-4 h-4 mr-2" />
              Teams
            </Button>
            <Button
              onClick={() => setActiveTab('bookmakers')}
              variant={activeTab === 'bookmakers' ? 'default' : 'outline'}
              className={activeTab === 'bookmakers' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              <Table className="w-4 h-4 mr-2" />
              Bookmakers
            </Button>
            <Button
              onClick={() => setActiveTab('markets')}
              variant={activeTab === 'markets' ? 'default' : 'outline'}
              className={activeTab === 'markets' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              <Table className="w-4 h-4 mr-2" />
              Markets
            </Button>
          </div>

          {/* Sync Tab */}
          {activeTab === 'sync' && (
            <LeagueSelector onSyncComplete={fetchData} />
          )}

          {/* Leagues Tab */}
          {activeTab === 'leagues' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">Leagues</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSyncAPIFootball}
                      disabled={syncing}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-green-600/50"
                      title="Update leagues and teams from API-Football"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                      {syncing ? 'Syncing...' : 'Update from API-Football'}
                    </Button>
                    <Dialog open={leagueDialog} onOpenChange={setLeagueDialog}>
                      <DialogTrigger asChild>
                        <Button
                          onClick={() => setLeagueForm({ id: null, name: '', country: '', sport: 'Football' })}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add League
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="glass-dark border-white/10">
                        <DialogHeader>
                          <DialogTitle className="text-white">{leagueForm.id ? 'Edit' : 'Add'} League</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-gray-300">League Name</Label>
                            <Input
                              value={leagueForm.name}
                              onChange={(e) => setLeagueForm({ ...leagueForm, name: e.target.value })}
                              className="bg-white/5 border-white/10 text-white"
                              placeholder="Premier League"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-300">Country</Label>
                            <Input
                              value={leagueForm.country}
                              onChange={(e) => setLeagueForm({ ...leagueForm, country: e.target.value })}
                              className="bg-white/5 border-white/10 text-white"
                              placeholder="England"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-300">Sport</Label>
                            <Input
                              value={leagueForm.sport}
                              onChange={(e) => setLeagueForm({ ...leagueForm, sport: e.target.value })}
                              className="bg-white/5 border-white/10 text-white"
                              placeholder="Football"
                            />
                          </div>
                          <Button onClick={handleSaveLeague} className="w-full bg-blue-600 hover:bg-blue-700">
                            {leagueForm.id ? 'Update' : 'Create'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-gray-300 p-3">Name</th>
                        <th className="text-left text-gray-300 p-3">Country</th>
                        <th className="text-left text-gray-300 p-3">Sport</th>
                        <th className="text-right text-gray-300 p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leagues.map((league, index) => (
                        <tr key={league.id} className="border-b border-white/5">
                          <td className="text-white p-3">{league.name}</td>
                          <td className="text-gray-300 p-3">{league.country}</td>
                          <td className="text-gray-300 p-3">{league.sport}</td>
                          <td className="text-right p-3">
                            <Button
                              onClick={() => handleMoveItem('leagues', leagues, setLeagues, index, 'up')}
                              variant="ghost"
                              size="sm"
                              disabled={index === 0}
                              className="text-gray-400 hover:text-white mr-1"
                              title="Move up"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleMoveItem('leagues', leagues, setLeagues, index, 'down')}
                              variant="ghost"
                              size="sm"
                              disabled={index === leagues.length - 1}
                              className="text-gray-400 hover:text-white mr-2"
                              title="Move down"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => {
                                setLeagueForm({ id: league.id, name: league.name, country: league.country, sport: league.sport });
                                setLeagueDialog(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300 mr-2"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteLeague(league.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {leagues.length === 0 && (
                    <p className="text-gray-400 text-center py-8">No leagues found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Teams Tab - Will be similar structure */}
          {activeTab === 'teams' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">Teams</CardTitle>
                  <Dialog open={teamDialog} onOpenChange={setTeamDialog}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => setTeamForm({ id: null, name: '', league_id: '' })}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Team
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass-dark border-white/10">
                      <DialogHeader>
                        <DialogTitle className="text-white">{teamForm.id ? 'Edit' : 'Add'} Team</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-gray-300">Team Name</Label>
                          <Input
                            value={teamForm.name}
                            onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="Manchester United"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">League</Label>
                          <Select value={teamForm.league_id} onValueChange={(value) => setTeamForm({ ...teamForm, league_id: value })}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white">
                              <SelectValue placeholder="Select league" />
                            </SelectTrigger>
                            <SelectContent>
                              {leagues.map((league) => (
                                <SelectItem key={league.id} value={league.id}>
                                  {league.name} ({league.country})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleSaveTeam} className="w-full bg-blue-600 hover:bg-blue-700">
                          {teamForm.id ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-gray-300 p-3">Name</th>
                        <th className="text-left text-gray-300 p-3">League</th>
                        <th className="text-left text-gray-300 p-3">Country</th>
                        <th className="text-right text-gray-300 p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teams.map((team) => (
                        <tr key={team.id} className="border-b border-white/5">
                          <td className="text-white p-3">{team.name}</td>
                          <td className="text-gray-300 p-3">{team.league_name}</td>
                          <td className="text-gray-300 p-3">{team.country}</td>
                          <td className="text-right p-3">
                            <Button
                              onClick={() => {
                                setTeamForm({ id: team.id, name: team.name, league_id: team.league_id });
                                setTeamDialog(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300 mr-2"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteTeam(team.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {teams.length === 0 && (
                    <p className="text-gray-400 text-center py-8">No teams found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bookmakers Tab */}
          {activeTab === 'bookmakers' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">Bookmakers</CardTitle>
                  <Dialog open={bookmakerDialog} onOpenChange={setBookmakerDialog}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => setBookmakerForm({ id: null, name: '' })}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Bookmaker
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass-dark border-white/10">
                      <DialogHeader>
                        <DialogTitle className="text-white">{bookmakerForm.id ? 'Edit' : 'Add'} Bookmaker</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-gray-300">Bookmaker Name</Label>
                          <Input
                            value={bookmakerForm.name}
                            onChange={(e) => setBookmakerForm({ ...bookmakerForm, name: e.target.value })}
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="Bet365"
                          />
                        </div>
                        <Button onClick={handleSaveBookmaker} className="w-full bg-blue-600 hover:bg-blue-700">
                          {bookmakerForm.id ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-gray-300 p-3">Name</th>
                        <th className="text-right text-gray-300 p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookmakers.map((bookmaker, index) => (
                        <tr key={bookmaker.id} className="border-b border-white/5">
                          <td className="text-white p-3">{bookmaker.name}</td>
                          <td className="text-right p-3">
                            <Button
                              onClick={() => handleMoveItem('bookmakers', bookmakers, setBookmakers, index, 'up')}
                              variant="ghost"
                              size="sm"
                              disabled={index === 0}
                              className="text-gray-400 hover:text-white mr-1"
                              title="Move up"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleMoveItem('bookmakers', bookmakers, setBookmakers, index, 'down')}
                              variant="ghost"
                              size="sm"
                              disabled={index === bookmakers.length - 1}
                              className="text-gray-400 hover:text-white mr-2"
                              title="Move down"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => {
                                setBookmakerForm({ id: bookmaker.id, name: bookmaker.name });
                                setBookmakerDialog(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300 mr-2"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteBookmaker(bookmaker.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bookmakers.length === 0 && (
                    <p className="text-gray-400 text-center py-8">No bookmakers found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Markets Tab */}
          {activeTab === 'markets' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">Markets</CardTitle>
                  <Dialog open={marketDialog} onOpenChange={setMarketDialog}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => setMarketForm({ id: null, name: '', description: '' })}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Market
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass-dark border-white/10">
                      <DialogHeader>
                        <DialogTitle className="text-white">{marketForm.id ? 'Edit' : 'Add'} Market</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-gray-300">Market Name</Label>
                          <Input
                            value={marketForm.name}
                            onChange={(e) => setMarketForm({ ...marketForm, name: e.target.value })}
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="Over/Under 2.5"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Description (Optional)</Label>
                          <Textarea
                            value={marketForm.description}
                            onChange={(e) => setMarketForm({ ...marketForm, description: e.target.value })}
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="Total goals over or under 2.5"
                          />
                        </div>
                        <Button onClick={handleSaveMarket} className="w-full bg-blue-600 hover:bg-blue-700">
                          {marketForm.id ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-gray-300 p-3">Name</th>
                        <th className="text-left text-gray-300 p-3">Description</th>
                        <th className="text-right text-gray-300 p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {markets.map((market, index) => (
                        <tr key={market.id} className="border-b border-white/5">
                          <td className="text-white p-3">{market.name}</td>
                          <td className="text-gray-300 p-3">{market.description || '-'}</td>
                          <td className="text-right p-3">
                            <Button
                              onClick={() => handleMoveItem('markets', markets, setMarkets, index, 'up')}
                              variant="ghost"
                              size="sm"
                              disabled={index === 0}
                              className="text-gray-400 hover:text-white mr-1"
                              title="Move up"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleMoveItem('markets', markets, setMarkets, index, 'down')}
                              variant="ghost"
                              size="sm"
                              disabled={index === markets.length - 1}
                              className="text-gray-400 hover:text-white mr-2"
                              title="Move down"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => {
                                setMarketForm({ id: market.id, name: market.name, description: market.description || '' });
                                setMarketDialog(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300 mr-2"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteMarket(market.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {markets.length === 0 && (
                    <p className="text-gray-400 text-center py-8">No markets found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText="Confirm"
        cancelText="Cancel"
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
      />
    </Layout>
  );
};

export default AdminTables;
